import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Lightbulb, MapPin, Mic, Square, Type, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { useLocation } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { SERVER_URL } from '../../utils/serverConfig';

const MAX_MS = 60_000;

function pageLabel(path) {
  const p = String(path || '').split('?')[0].replace(/\/+$/, '') || '/';
  const last = p.split('/').filter(Boolean).pop() || 'Dashboard';
  return last.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

const FloatingSuggestButton = () => {
  const api = useApi();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [mode, setMode] = useState('text');
  const [body, setBody] = useState('');
  const [marker, setMarker] = useState('');
  const [sending, setSending] = useState(false);
  const [audioUsed, setAudioUsed] = useState(false);
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const recRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const startedAt = useRef(0);
  const tickRef = useRef(null);
  const blobRef = useRef(null);

  const loadQuota = useCallback(async () => {
    try {
      const res = await api.get('/api/v1/admin-suggestions/quota');
      setAudioUsed(Boolean(res.data?.data?.audioUsedToday));
    } catch (_e) { /* ignore */ }
  }, [api]);

  useEffect(() => {
    if (open) loadQuota();
  }, [open, loadQuota]);

  const stopRec = useCallback(() => {
    if (tickRef.current) clearInterval(tickRef.current);
    tickRef.current = null;
    const rec = recRef.current;
    recRef.current = null;
    return new Promise((resolve) => {
      if (!rec || rec.state === 'inactive') {
        setRecording(false);
        resolve(blobRef.current);
        return;
      }
      rec.onstop = () => {
        streamRef.current?.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        blobRef.current = new Blob(chunksRef.current, { type: rec.mimeType || 'audio/webm' });
        setRecording(false);
        resolve(blobRef.current);
      };
      rec.stop();
    });
  }, []);

  useEffect(() => () => stopRec(), [stopRec]);

  const startRec = async () => {
    if (audioUsed) {
      toast.error('One audio note per day. Use text instead.');
      return;
    }
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        toast.error('Microphone is not supported in this browser.');
        return;
      }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported('audio/webm;codecs=opus'))
        ? 'audio/webm;codecs=opus'
        : (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported('audio/webm'))
          ? 'audio/webm'
          : '';
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      chunksRef.current = [];
      blobRef.current = null;
      rec.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      rec.start();
      recRef.current = rec;
      startedAt.current = Date.now();
      setElapsed(0);
      setRecording(true);
      setMode('audio');
      tickRef.current = setInterval(() => {
        const ms = Date.now() - startedAt.current;
        setElapsed(ms);
        if (ms >= MAX_MS) stopRec();
      }, 200);
    } catch (e) {
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      const name = e?.name || '';
      if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
        toast.error('Allow the microphone in the browser address bar, then tap again.');
      } else if (name === 'NotFoundError') {
        toast.error('No microphone found.');
      } else {
        toast.error(e?.message || 'Could not start the microphone.');
      }
    }
  };

  const reset = () => {
    stopRec();
    blobRef.current = null;
    setBody('');
    setMarker('');
    setMode('text');
    setElapsed(0);
  };

  const submit = async () => {
    const pagePath = location.pathname + (location.search || '');
    const fd = new FormData();
    fd.append('pagePath', pagePath);
    fd.append('pageTitle', document.title || '');
    fd.append('marker', marker.trim());
    if (mode === 'audio') {
      const blob = recording ? await stopRec() : blobRef.current;
      if (!blob) {
        toast.error('Record an audio note first.');
        return;
      }
      fd.append('type', 'audio');
      fd.append('durationMs', String(elapsed || Date.now() - startedAt.current));
      fd.append('audio', blob, 'suggestion.webm');
      if (body.trim()) fd.append('body', body.trim());
    } else {
      if (!body.trim()) {
        toast.error('Write a suggestion first.');
        return;
      }
      fd.append('type', 'text');
      fd.append('body', body.trim());
    }
    setSending(true);
    try {
      const res = await fetch(`${SERVER_URL}/api/v1/admin-suggestions`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: fd,
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.error?.message || json?.message || 'Could not send suggestion.');
      toast.success('Suggestion sent to TeamGrid.');
      reset();
      setOpen(false);
      loadQuota();
    } catch (e) {
      toast.error(e.message || 'Could not send suggestion.');
    } finally {
      setSending(false);
    }
  };

  const secs = Math.max(0, Math.floor(elapsed / 1000));

  return (
    <>
      <motion.div
        style={{
          position: 'fixed',
          bottom: 88,
          right: 24,
          zIndex: 50,
        }}
      >
        <motion.button
          type="button"
          onClick={() => setOpen((v) => !v)}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          className="rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#00A89B] touch-manipulation"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: hovered ? 10 : 0,
            height: 52,
            padding: hovered ? '0 18px 0 12px' : 0,
            width: hovered ? 'auto' : 52,
            justifyContent: 'center',
            background: 'linear-gradient(145deg, #1c2430 0%, #11161e 100%)',
            border: '1px solid rgba(255,255,255,0.08)',
            boxShadow: hovered
              ? '0 14px 30px -10px rgba(17,22,30,0.55), 0 3px 8px -3px rgba(20,12,10,0.28)'
              : '0 8px 20px -8px rgba(17,22,30,0.45), 0 2px 5px -2px rgba(20,12,10,0.22)',
            transition: 'padding 240ms cubic-bezier(0.16, 1, 0.3, 1), box-shadow 200ms ease',
            WebkitTapHighlightColor: 'transparent',
            userSelect: 'none',
            cursor: 'pointer',
          }}
          whileHover={{ y: -2 }}
          whileTap={{ scale: 0.97, y: 0 }}
          aria-label="Suggest to TeamGrid"
        >
          <Lightbulb size={20} strokeWidth={2.2} color="#00D1C1" style={{ flexShrink: 0 }} />
          <motion.span
            initial={false}
            animate={{ width: hovered ? 'auto' : 0, opacity: hovered ? 1 : 0 }}
            transition={{ duration: 0.24, ease: [0.16, 1, 0.3, 1] }}
            style={{
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              fontSize: 14,
              fontWeight: 600,
              letterSpacing: '0.01em',
              color: '#ffffff',
            }}
          >
            Suggest
          </motion.span>
        </motion.button>
      </motion.div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 12, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.98 }}
            transition={{ duration: 0.18 }}
            style={{
              position: 'fixed',
              right: 24,
              bottom: 152,
              zIndex: 60,
              width: 360,
              maxWidth: 'calc(100vw - 32px)',
              background: '#14161c',
              color: '#f0f2f6',
              border: '1px solid #252a35',
              borderRadius: 18,
              boxShadow: '0 22px 46px -16px rgba(0,0,0,0.65)',
              padding: 16,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 650 }}>Suggest to TeamGrid</div>
                <div style={{ fontSize: 12, color: '#9aa1b0', marginTop: 2 }}>Notes or one audio a day · this page is attached</div>
              </div>
              <button type="button" onClick={() => { reset(); setOpen(false); }} aria-label="Close" style={{ background: 'none', border: 0, color: '#9aa1b0', cursor: 'pointer' }}>
                <X size={18} />
              </button>
            </div>

            <div style={{
              display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#c5cbd6',
              background: '#1b1f28', border: '1px solid #252a35', borderRadius: 10, padding: '8px 10px', marginBottom: 10,
            }}>
              <MapPin size={13} color="#00D1C1" />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {pageLabel(location.pathname)} · {location.pathname}
              </span>
            </div>

            <input
              value={marker}
              onChange={(e) => setMarker(e.target.value.slice(0, 80))}
              placeholder="Marker (optional) — e.g. filter chips"
              style={{
                width: '100%', boxSizing: 'border-box', marginBottom: 10,
                background: '#1b1f28', border: '1px solid #252a35', borderRadius: 10,
                color: '#f0f2f6', fontSize: 13, padding: '9px 10px', outline: 'none',
              }}
            />

            <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
              <button
                type="button"
                onClick={() => { stopRec(); setMode('text'); }}
                style={{
                  flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  height: 34, borderRadius: 9, cursor: 'pointer', fontSize: 12, fontWeight: 600,
                  background: mode === 'text' ? 'rgba(0,209,193,0.14)' : '#1b1f28',
                  color: mode === 'text' ? '#3fe0d2' : '#9aa1b0',
                  border: `1px solid ${mode === 'text' ? 'rgba(0,209,193,0.35)' : '#252a35'}`,
                }}
              >
                <Type size={13} /> Text
              </button>
              <button
                type="button"
                onClick={() => setMode('audio')}
                disabled={audioUsed}
                style={{
                  flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  height: 34, borderRadius: 9, cursor: audioUsed ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 600,
                  opacity: audioUsed ? 0.5 : 1,
                  background: mode === 'audio' ? 'rgba(0,209,193,0.14)' : '#1b1f28',
                  color: mode === 'audio' ? '#3fe0d2' : '#9aa1b0',
                  border: `1px solid ${mode === 'audio' ? 'rgba(0,209,193,0.35)' : '#252a35'}`,
                }}
              >
                <Mic size={13} /> Audio {audioUsed ? '(used today)' : '(1/day)'}
              </button>
            </div>

            {mode === 'text' ? (
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value.slice(0, 4000))}
                placeholder="What should we change on this page?"
                rows={4}
                style={{
                  width: '100%', boxSizing: 'border-box', resize: 'none',
                  background: '#1b1f28', border: '1px solid #252a35', borderRadius: 10,
                  color: '#f0f2f6', fontSize: 13, padding: 10, outline: 'none', marginBottom: 12,
                }}
              />
            ) : (
              <div style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ fontSize: 12, color: recording ? '#f0736c' : '#9aa1b0' }}>
                    {recording ? `Recording ${secs}s / 60s` : blobRef.current || elapsed ? `Ready · ${secs}s` : 'Tap to record'}
                  </span>
                  <button
                    type="button"
                    onClick={recording ? stopRec : startRec}
                    style={{
                      width: 40, height: 40, borderRadius: 20, border: 0, cursor: 'pointer',
                      background: recording ? '#e5514b' : '#00A89B', color: '#fff',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    {recording ? <Square size={14} /> : <Mic size={16} />}
                  </button>
                </div>
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value.slice(0, 4000))}
                  placeholder="Optional caption"
                  rows={2}
                  style={{
                    width: '100%', boxSizing: 'border-box', resize: 'none',
                    background: '#1b1f28', border: '1px solid #252a35', borderRadius: 10,
                    color: '#f0f2f6', fontSize: 13, padding: 10, outline: 'none',
                  }}
                />
              </div>
            )}

            <button
              type="button"
              onClick={submit}
              disabled={sending}
              style={{
                width: '100%', height: 40, borderRadius: 10, border: 0, cursor: sending ? 'wait' : 'pointer',
                background: '#00A89B', color: '#fff', fontWeight: 650, fontSize: 13,
                opacity: sending ? 0.7 : 1,
              }}
            >
              {sending ? 'Sending…' : 'Send suggestion'}
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default FloatingSuggestButton;
